// ─────────────────────────────────────────────────────────────────────────────
// Report export — any Metrics & Reporting report as a printed page or a Word
// document, sharing the grievance export's document furniture so a binder
// holding both does not look like it came from two products.
//
// WHY IT CAPTURES THE RENDERED REPORT RATHER THAN REBUILDING IT.
//
// There are nine grievance reports plus everything ReportEngine renders from a
// declarative definition, and more get added by writing a def. Rebuilding each
// one as a document would be a second implementation of every report, and the
// two would drift — the printed copy quietly disagreeing with the screen is
// exactly the failure that matters for a document somebody files. Reports here
// draw their bars and tables as ordinary styled divs, no canvas, so the
// rendered DOM already IS the report. Lift it, restyle it for paper, done: a
// report added next month exports with no export code written for it.
//
// WHAT IS STRIPPED. Controls — buttons, inputs, selects — and anything marked
// data-noexport. A printed report with a dead "Refresh" button on it reads as a
// screenshot, which is what this exists to stop people sending.
//
// ON THE AUTHOR. A report is not created the way a grievance record is; it is
// run. So the author is the person who ran it, named in the Word properties and
// in the footer, alongside the facility scope and the date. A report that does
// not say who ran it, over what scope, on what day is not evidence of anything.
// ─────────────────────────────────────────────────────────────────────────────

function esc(v) {
  if (v === null || v === undefined) return "";
  return String(v)
    .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;").replace(/'/g, "&#39;");
}

function longDate(iso) {
  const d = iso ? new Date(iso) : new Date();
  if (isNaN(d.getTime())) return String(iso || "");
  return d.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
}

/** A filesystem-safe basename, no extension. */
export function reportFileBase(title, facilityName) {
  return ["Report", title, facilityName, new Date().toISOString().slice(0, 10)]
    .map((p) => String(p || "").replace(/[^A-Za-z0-9._-]+/g, "-"))
    .filter(Boolean)
    .join("_")
    .replace(/-+/g, "-")
    .slice(0, 120);
}

/**
 * The report's rendered markup, with interactive furniture removed.
 * Works on a CLONE, so nothing on screen is disturbed.
 */
export function captureReportHtml(node) {
  if (!node) return "";
  const clone = node.cloneNode(true);
  clone.querySelectorAll("button, input, select, textarea, [data-noexport]").forEach((el) => el.remove());
  return clone.innerHTML;
}

/**
 * @param {object} o
 * @param {string} o.title
 * @param {string} [o.subtitle]
 * @param {string} o.bodyHtml        from captureReportHtml
 * @param {string} [o.facilityName]  the scope this report was run over
 * @param {string} [o.generatedBy]   who ran it — the author
 * @param {string} [o.source]        "pcc" | "mock"
 * @param {boolean} [o.forWord]
 * @param {boolean} [o.autoPrint]
 */
export function buildReportDocument(o = {}) {
  const {
    title = "Report", subtitle = "", bodyHtml = "",
    facilityName = "", generatedBy = "", source = "",
    forWord = false, autoPrint = false,
  } = o;

  // SAY WHICH DATA THIS IS. A sample-data report printed with no mark on it
  // becomes a real report the moment it leaves the building, and there is no
  // way to tell afterwards. Live data gets a quiet line; sample data gets a
  // banner that survives a photocopy.
  const sampleBanner = source === "mock"
    ? `<div class="sample">SAMPLE DATA — illustrative figures, not this facility's records. Not for submission, posting or any regulatory use.</div>`
    : "";

  const officeProps = forWord ? `
<!--[if gte mso 9]><xml>
 <o:DocumentProperties>
  <o:Title>${esc(title)}</o:Title>
  <o:Author>${esc(generatedBy)}</o:Author>
  <o:LastAuthor>${esc(generatedBy)}</o:LastAuthor>
  <o:Company>${esc(facilityName)}</o:Company>
  <o:Subject>${esc(subtitle || title)}</o:Subject>
 </o:DocumentProperties>
</xml><![endif]-->` : "";

  // The on-screen report is laid out for a wide viewport: auto-fit grids that
  // make three columns of cards, and flex rows of KPI tiles. On US Letter that
  // has to come down to two, or the type ends up at six points. Rather than
  // fight each report's inline styles, the rules below override the two layout
  // primitives every report is built from.
  const css = `
  @page { size: letter; margin: 0.7in 0.6in; }
  * { -webkit-print-color-adjust: exact; print-color-adjust: exact; box-sizing: border-box; }
  body { font-family: Calibri, Carlito, Arial, sans-serif; color: #1a1a1a; font-size: 10pt; line-height: 1.45; margin: 0; }

  .head { border-bottom: 2px solid #0f4c81; padding-bottom: 10px; margin-bottom: 8px; }
  .brand { font-size: 8.5pt; letter-spacing: .08em; text-transform: uppercase; color: #0f4c81; font-weight: bold; }
  h1.doc { font-size: 18pt; margin: 4px 0 2px; color: #0f172a; }
  .sub { font-size: 10pt; color: #444; }
  .scope { font-size: 9pt; color: #555; margin-top: 5px; }
  .sample { background: #fffbeb; border: 1.5px solid #b45309; color: #7c2d12; font-weight: bold;
            font-size: 9pt; padding: 7px 10px; margin: 10px 0 14px; letter-spacing: .01em; }

  /* The report's own markup, restyled for paper. */
  .body { margin-top: 12px; }
  .body .card { background: #fff !important; border: 1px solid #c8d6e3 !important; border-radius: 0 !important;
                padding: 11px 13px !important; page-break-inside: avoid; }
  .body .section-header { font-size: 8.5pt !important; font-weight: bold; color: #0f4c81 !important;
                          text-transform: uppercase; letter-spacing: .06em;
                          border-bottom: 1px solid #c8d6e3 !important; padding-bottom: 5px !important;
                          margin-bottom: 9px !important; page-break-after: avoid; }
  .body .badge { border-radius: 2px !important; font-size: 8pt !important; padding: 1px 6px !important; }
  .body h2 { font-size: 12pt; page-break-after: avoid; }
  .body h3 { font-size: 10pt; page-break-after: avoid; }
  .body table { width: 100%; border-collapse: collapse; font-size: 9pt !important; }
  .body th, .body td { padding: 4px 7px !important; }
  .body thead { display: table-header-group; }   /* repeat headers across pages */
  .body tr { page-break-inside: avoid; }

  /* Bring wide screen layouts down to paper. Every report is built from one of
     these two primitives, so overriding them here covers all of them. */
  .body div[style*="grid-template-columns"] { grid-template-columns: 1fr 1fr !important; gap: 12px !important; }
  .body div[style*="minmax"] { grid-template-columns: 1fr 1fr !important; }

  .foot { border-top: 1px solid #c8d6e3; margin-top: 20px; padding-top: 8px; font-size: 8.5pt; color: #555; }
  .conf { font-weight: bold; color: #b3261e; margin-bottom: 3px; }`;

  const body = `
<div class="head">
  <div class="brand">SurvAIHealth · Compliance Suite</div>
  <h1 class="doc">${esc(title)}</h1>
  ${subtitle ? `<div class="sub">${esc(subtitle)}</div>` : ""}
  <div class="scope">${esc(facilityName || "All facilities")} · Run ${esc(longDate())}${generatedBy ? " by " + esc(generatedBy) : ""}</div>
</div>
${sampleBanner}
<div class="body">${bodyHtml}</div>
<div class="foot">
  <div class="conf">CONFIDENTIAL — may contain resident information. Handle, store and dispose of under the
    facility's privacy policy and 45 CFR Parts 160 and 164.</div>
  <div>${esc(title)} · ${esc(facilityName || "All facilities")} · ${esc(longDate())}${generatedBy ? " · Run by " + esc(generatedBy) : ""} · SurvAIHealth Compliance Suite</div>
</div>`;

  const wordNs = forWord
    ? ' xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:w="urn:schemas-microsoft-com:office:word" xmlns="http://www.w3.org/TR/REC-html40"'
    : "";
  const meta = generatedBy ? `<meta name="author" content="${esc(generatedBy)}">` : "";
  const script = autoPrint
    ? "<script>window.onload=function(){setTimeout(function(){window.print();},150);};<\/script>"
    : "";

  return `<!DOCTYPE html><html${wordNs}><head><meta charset="utf-8">
<title>${esc(title)}</title>${meta}${officeProps}
<style>${css}</style></head><body>${body}${script}</body></html>`;
}

/** Returns null on success, or a message to show the user. */
export function printReport(node, opts = {}) {
  const bodyHtml = captureReportHtml(node);
  const html = buildReportDocument({ ...opts, bodyHtml, forWord: false, autoPrint: true });
  const w = window.open("", "_blank");
  if (!w) return "Your browser blocked the print window. Allow pop-ups for this site, then try again.";
  w.document.write(html);
  w.document.close();
  return null;
}

export function downloadReportDoc(node, opts = {}) {
  const bodyHtml = captureReportHtml(node);
  const html = buildReportDocument({ ...opts, bodyHtml, forWord: true, autoPrint: false });
  // The BOM keeps Word from guessing the encoding and mangling any non-ASCII
  // character in a facility or resident name.
  const blob = new Blob(["﻿", html], { type: "application/msword" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${reportFileBase(opts.title, opts.facilityName)}.doc`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
  return null;
}
